import { Amplify } from "aws-amplify";
import { events } from 'aws-amplify/data';

const RTC_ENDPOINT = process.env.RTC_EP;
const RTC_REGION = 'eu-south-1';
const RTC_AUTH_TOKEN = 'custom-authorized';
const RTC_NAMESPACE = '/application';

let isInitialized = false;

/**
 * Initialize AWS Amplify configuration for Events API
 * Should be called once at application startup
 */
export const initializeAmplify = () => {
    console.log('Initializing Amplify Events');
    console.log('RTC_ENDPOINT:', RTC_ENDPOINT);

    if (isInitialized) {
        console.warn('Amplify already initialized');
        return;
    }

    if (!RTC_ENDPOINT) {
        console.error('RTC_ENDPOINT environment variable is not set');
        throw new Error('RTC_ENDPOINT is required for Amplify Events');
    }

    Amplify.configure({
        API: {
            Events: {
                endpoint: RTC_ENDPOINT,
                region: RTC_REGION,
                defaultAuthMode: 'lambda'
            },
        },
    });

    isInitialized = true;
    console.log('Amplify Events configured:', { endpoint: RTC_ENDPOINT, region: RTC_REGION });
};

/**
 * Get the authentication token for Lambda authorizer
 */
export const getAuthToken = (): string => RTC_AUTH_TOKEN;

/**
 * Get the base namespace for channels
 */
export const getNamespace = (): string => RTC_NAMESPACE;

/**
 * Sanitize session ID for use in AppSync Events channel names
 * Replaces spaces and special characters with hyphens
 */
const sanitizeSessionId = (sessionId: string): string => {
    return sessionId
        .replace(/\s+/g, '-')           // Replace spaces with hyphens
        .replace(/[^\w-]/g, '-')        // Replace non-word chars (except hyphens) with hyphens
        .replace(/-+/g, '-')            // Replace multiple consecutive hyphens with single hyphen
        .replace(/^-|-$/g, '');         // Remove leading/trailing hyphens
};

/**
 * Extract the game identifier from a full gameId
 */
export const extractGameIdentifier = (gameId: string): string => {
    const parts = gameId.split('::');
    if (parts.length < 2) return sanitizeSessionId(parts[0]);

    const teamPart = parts[1].split(':')[0];
    return `${sanitizeSessionId(parts[0])}-${teamPart}`;
}

/**
 * Extract session ID from a gameId
 */
export const extractSessionId = (gameId: string): string => {
    const parts = gameId.split('::');
    return sanitizeSessionId(parts[0]);
};

/**
 * Publish an event to AppSync Events channel
 * This is used by the frontend to publish events for real-time updates
 */
export const publishEvent = async (channelPath: string, data: any): Promise<void> => {
    try {
        const channelName = `${RTC_NAMESPACE}${channelPath}`;
        console.log('📤 [Amplify] Publishing event to channel:', channelName);

        await events.post(channelName, data, { authToken: RTC_AUTH_TOKEN });

        console.log('✅ [Amplify] Event published successfully');
    } catch (error: any) {
        console.error('❌ [Amplify] Failed to publish event:', error.message || error);
        // Don't throw - we don't want to fail the operation if publishing fails
    }
};
